
import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { createProfile, getUserOnboardingProgress, saveOnboardingProgress as saveProgressToDb } from "@/lib/api-adapter";
import { OnboardingState } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface User {
  email: string;
  fullname?: string | null;
  whatsapp?: string | null;
  tiktokusername?: string | null;
  savedstep?: OnboardingState | null;
}

interface AuthContextProps {
  user: User | null;
  checkUserExists: (email: string) => Promise<{exists: boolean, progress?: OnboardingState | null}>;
  saveUserInfo: (email: string, name?: string, whatsapp?: string, username?: string) => Promise<boolean>;
  saveOnboardingProgress: (email: string, state: OnboardingState) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Initialize user session from stored email (if any)
    const storedEmail = localStorage.getItem('user_email');
    
    if (storedEmail) {
      console.log("Found stored email:", storedEmail);
      
      // Fetch the latest progress from the server
      getUserOnboardingProgress(storedEmail)
        .then(({ exists, progress }) => {
          if (exists) {
            console.log("Found saved progress on server:", progress);
            setUser({
              email: storedEmail,
              savedstep: progress
            });
          } else {
            console.log("No progress found for stored email");
            setUser({
              email: storedEmail
            });
          }
        })
        .catch(error => {
          console.error("Error initializing user session:", error);
        });
    }
  }, []);

  const checkUserExists = async (email: string): Promise<{exists: boolean, progress?: OnboardingState | null}> => {
    console.log("Checking if user exists:", email);
    
    try {
      // Store email in localStorage for session persistence
      localStorage.setItem('user_email', email);
      
      // Check if user exists in the database and get their progress
      const { exists, progress } = await getUserOnboardingProgress(email);
      
      if (exists) {
        console.log("User found with progress:", progress);
        
        setUser({
          email,
          savedstep: progress
        });
        
        return { exists: true, progress };
      }
      
      // User doesn't exist yet, but we have their email
      setUser({ email });
      console.log("New user, storing email");
      
      return { exists: false };
    } catch (error) {
      console.error("Error checking if user exists:", error);
      toast.error("Error checking user account");
      return { exists: false };
    }
  };

  const saveUserInfo = async (
    email: string, 
    name?: string, 
    whatsapp?: string,
    username?: string
  ): Promise<boolean> => {
    try {
      console.log("Saving user info:", { email, name, whatsapp, username });
      
      // Update user state with new info
      setUser(prevUser => ({
        ...prevUser || { email },
        email,
        fullname: name || prevUser?.fullname,
        whatsapp: whatsapp || prevUser?.whatsapp,
        tiktokusername: username || prevUser?.tiktokusername
      }));
      
      // Save to Supabase
      await createProfile(email, name, whatsapp, username);
      
      return true;
    } catch (error) {
      console.error("Error saving user info:", error);
      return false;
    }
  };

  const saveOnboardingProgress = async (email: string, state: OnboardingState): Promise<boolean> => {
    try {
      console.log("Saving onboarding progress for email:", email);
      
      if (!email) {
        console.error("Missing email for saving progress");
        return false;
      }
      
      // Save progress to database
      const success = await saveProgressToDb(email, state);
      
      if (success) {
        // Update local user state with the new progress
        setUser(prevUser => ({
          ...prevUser || { email },
          email,
          savedstep: state
        }));
      }
      
      return success;
    } catch (error) {
      console.error("Error saving onboarding progress:", error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user_email');
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        checkUserExists, 
        saveUserInfo,
        saveOnboardingProgress,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
